// Centralized static UI text — labels, buttons, titles/tooltips, hints,
// placeholders and flash/toast messages. Currently covers the global toast
// system (http/index.ts, ToastContainer) and the "Получатели платежа" admin
// page (AdminPayees.tsx) — the areas actively being built when this file was
// introduced. Not (yet) a full i18n system: no language switching, just one
// place to find/edit the app's Russian/Ukrainian copy instead of it being
// scattered through JSX. Follow the existing per-file language convention
// (see CLAUDE.md) when adding new entries.
//
// Plain string constants where the text never changes; functions where the
// message needs to interpolate a value (a count, a joined error list, etc.).

export const locale = {
    // Bits reused verbatim across several unrelated places.
    common: {
        cancel: 'Отмена',
        confirm: 'Подтвердить',
        delete: 'Удалить',
        upload: 'Загрузить',
        noFile: 'нет файла',
        moveUp: 'Выше',
        moveDown: 'Ниже',
        deleteFailed: 'Не удалось удалить',
        emptyDash: '—',
    },

    // Global "operation succeeded/failed" popup — see ToastStore/
    // ToastContainer and the axios interceptors in http/index.ts. These are
    // only the *fallback* texts used when a response carries no message of
    // its own (a bare string or {message} from the backend wins first).
    toast: {
        genericSuccess: 'Операция выполнена успешно',
        genericError: 'Не удалось выполнить операцию',
    },

    // crmServer is a separate repo/deploy, and its responses mix Russian,
    // Ukrainian and English (`Payee not found`, `Company not found!`,
    // `UNAUTHORIZED`, …) depending on which controller/era wrote them —
    // see CLAUDE.md's http/*API.ts note: callers get the response body
    // verbatim. The toast is required to read in Russian regardless, so
    // every backend string actually seen in a toast gets an exact-match
    // translation here; `translateApiMessage` (below) is what the axios
    // interceptor runs it through. Anything not listed — a message this
    // dictionary hasn't caught up with yet — is shown as-is rather than
    // dropped, so a new/unmapped backend string is still visible (just not
    // guaranteed Russian) instead of silently disappearing.
    apiMessageTranslations: {
        // Auth / generic HTTP (AuthMiddleware, RolesMiddleware, ErrorHandlerMiddleware)
        'UNAUTHORIZED': 'Не авторизовано — войдите заново',
        'FORBIDDEN': 'Недостаточно прав',
        'SERVER ERROR': 'Ошибка сервера',
        'BAD REQUEST': 'Некорректный запрос',

        // Users
        'No such user': 'Пользователь не найден',
        'Користвуча не знайдено': 'Пользователь не найден',
        'Користувача не знайдено': 'Пользователь не найден',
        'Can not delete yourself': 'Нельзя удалить самого себя',
        'Cannot delete yourself': 'Нельзя удалить самого себя',

        // Companies / representatives
        'Company not found': 'Предприятие не найдено',
        'Company not found!': 'Предприятие не найдено',
        'Company successfully deleted': 'Предприятие удалено',
        'Representative not found': 'Представитель не найден',
        'Representative deleted': 'Представитель удалён',
        'Representative added successfully': 'Представитель добавлен',
        'Error during adding representative': 'Ошибка при добавлении представителя',

        // Suppliers
        'Supplier not found': 'Поставщик не найден',
        'Supplier not found!': 'Поставщик не найден',
        'Supplier successfully deleted': 'Поставщик удалён',

        // Tasks
        'Task added successfully': 'Задача добавлена',
        'task deleted': 'Задача удалена',
        'Task updated': 'Задача обновлена',

        // Calculations
        'No required parameters': 'Не переданы обязательные параметры',
        'Invalid parameters': 'Некорректные параметры',
        'Query parameters not valid': 'Некорректные параметры запроса',
        'calculation ID is required': 'Не указан ID расчёта',
        'Calculation ID is required': 'Не указан ID расчёта',
        'calculation not found': 'Расчёт не найден',
        'Calculation not found': 'Расчёт не найден',
        'Calculation does not exist': 'Расчёт не найден',
        'ID or Data has not been given': 'Не переданы ID или данные',
        'Calculation added successfully': 'Расчёт добавлен',
        'calculation deleted': 'Расчёт удалён',
        'Calc updated': 'Расчёт обновлён',
        'Calculation has been updated': 'Расчёт обновлён',

        // Payees / calc tables
        'Payee not found': 'Получатель платежа не найден',
        'Payee deleted': 'Получатель платежа удалён',
        'payeeId is required': 'Не указан получатель платежа',
        'Default payee updated': 'Получатель платежа по умолчанию обновлён',
        'Cannot delete the default payee — set another default first': 'Нельзя удалить получателя по умолчанию — сначала назначьте другого',
        'Cannot delete the only payee': 'Нельзя удалить единственного получателя платежа',
        'Name cannot be empty': 'Название не может быть пустым',
        'Name is required': 'Укажите название',
        'Unsupported image type': 'Неподдерживаемый формат изображения',
        'File is required (field "img")': 'Не выбран файл (поле "img")',
        'Calc table not found': 'Таблица расчёта не найдена',
        'Calc table activated': 'Таблица назначена текущей',
        'Calc table archived': 'Таблица архивирована',
        'Calc table unarchived': 'Таблица разархивирована',
        'Calc table deleted': 'Таблица удалена',

        // PDF
        'Default pdf data not found': 'Настройки PDF по умолчанию не найдены',
        'Server Error. Pdf not found': 'Ошибка сервера. PDF не найден',
        'Error during pdf generating': 'Ошибка при формировании PDF',
        'Pdf successfully created': 'PDF успешно создан',
        'Invalid template': 'Некорректный шаблон',

        // Generic fallbacks seen across several controllers
        'Server Error': 'Ошибка сервера',
        'Server error': 'Ошибка сервера',
        'ID is required': 'Не указан ID',
        'ID is required!': 'Не указан ID',
        'ID not found': 'ID не найден',
        'Not Found': 'Не найдено',
        'Successfully updated': 'Успешно обновлено',
        'Updated successfully': 'Успешно обновлено',
    } as { [rawMessage: string]: string },

    payeeAdmin: {
        pageTitle: 'Получатели платежа',
        createButton: 'Создать',
        invoiceCounterBadgeTitle: 'Номер последнего счёта-фактуры',
        defaultBadge: 'по умолчанию',
        newPayeeHeader: 'Новое юридическое лицо',

        toolbar: {
            saveRequisites: 'Сохранить реквизиты',
            saveRequisitesTitle: 'Сохраняет реквизиты получателя (не таблицу расчёта — у неё своя кнопка сохранения ниже)',
            makeDefault: 'Сделать по умолчанию',
            deleteDefaultBlockedTitle: 'Нельзя удалить получателя по умолчанию',
        },

        fields: {
            name: { label: 'Наименование*', placeholder: 'ФОП "ПРІЗВИЩЕ ІМ\'Я ПО БАТЬКОВІ"' },
            entityType: { label: 'Тип', placeholder: 'ФОП / ТОВ' },
            edrpou: { label: 'ЄДРПОУ' },
            vatId: { label: 'ІПН' },
            iban: { label: 'IBAN (розрахунковий рахунок)' },
            bankName: { label: 'Банк' },
            address: { label: 'Адрес' },
            defaultContract: { label: 'Договор (по умолчанию)' },
            signatureLine: {
                label: 'Строка у підпису (необязательно)',
                placeholder: 'Директор ТОВ "..."          Омельчак О.Є',
                hint: 'Показывается в счёте рядом с печатью и подписью. Пусто — строка не выводится. ' +
                    'Чтобы печать легла в разрыв между словами (например, между должностью и ПІБ), ' +
                    'вставьте побольше пробелов на нужном месте — они сохраняются как есть, не схлопываются.',
            },
            invoiceCounter: {
                label: 'Номер последнего счёта-фактуры',
                hint: 'Собственная нумерация этого юрлица. Следующий счёт получит номер +1. Инкрементируется автоматически при формировании накладной.',
                warn: 'Значение можно только увеличивать. Уменьшение приведёт к повторным номерам счетов и перезаписи уже сформированных PDF.',
            },
        },
        invoiceCounterMinSuffix: (min: number | string) => ` (не ниже ${min})`,

        vatPayerCheckbox: 'Плательщик НДС (показывать строки ПДВ в счёте)',

        images: {
            uploadHintBeforeCreate: 'Логотип, печать и подпись можно загрузить после создания записи.',
            logoLabel: 'Логотип',
            logoAlt: 'логотип',
            stampLabel: 'Печать',
            stampAlt: 'печать',
            signLabel: 'Подпись',
            signAlt: 'подпись',
        },

        selectOrCreateHint: 'Выберите получателя слева или создайте нового.',

        validation: {
            nameRequired: 'Укажите наименование',
            invoiceCounterMustIncrease: (current: number | string) =>
                `Номер счёта-фактуры можно только увеличивать (текущий — ${current})`,
            saveFailed: 'Не удалось сохранить',
            saved: 'Сохранено',
            makeDefaultFailed: 'Не удалось назначить',
            madeDefault: 'Назначен получателем по умолчанию',
            deleted: 'Удалено',
            uploadFailed: 'Не удалось загрузить',
            logoUpdated: 'Логотип обновлён',
            stampUpdated: 'Печать обновлена',
            signUpdated: 'Подпись обновлена',
        },

        tables: {
            sectionTitle: 'Таблицы расчёта',
            sectionHint: 'Таблица задаёт ставки и схему колонок расчёта. Компания использует ту таблицу, ' +
                'которая сейчас «текущая» у выбранного для неё получателя. Таблица, по которой ' +
                'уже есть хотя бы один расчёт, больше не редактируется (кроме названия) и не ' +
                'удаляется — только архивируется. Для новой логики создайте новую таблицу.',
            noneYetHint: 'Таблиц ещё нет — расчёты этого получателя используют встроенную схему по умолчанию.',
            emptyFilterHint: 'В этом фильтре пусто.',
            tabs: { all: 'Все', current: 'Текущая', archived: 'Архив' },
            badge: { current: 'текущая', used: 'используется', archived: 'архив', draft: 'черновик' },
            makeCurrentButton: 'Сделать текущей',
            renameButton: 'Переименовать',
            editButton: 'Редактировать',
            archiveButton: 'Архивировать',
            archiveCurrentBlockedTitle: 'Сначала назначьте другую текущей',
            unarchiveButton: 'Разархивировать',
            newTableButton: '＋ Новая таблица',
            saveTableButton: 'Сохранить таблицу',

            validation: {
                nameRequired: 'Укажите название таблицы',
                schemaErrors: (joined: string) => `Ошибки в схеме таблицы: ${joined}`,
                footerErrors: (joined: string) => `Ошибки в итоговых полях: ${joined}`,
                saveFailed: 'Не удалось сохранить таблицу',
                saved: 'Таблица сохранена',
                activateFailed: 'Не удалось назначить текущей',
                activated: 'Назначена текущей таблицей',
                archiveFailed: 'Не удалось архивировать',
                archived: 'Таблица архивирована',
                unarchiveFailed: 'Не удалось разархивировать',
                unarchived: 'Таблица разархивирована',
                deleted: 'Таблица удалена',
            },
        },

        tableEditor: {
            nameLabel: 'Название таблицы*',
            lockedWarning: '⚠ Таблица уже используется в расчётах — ставки и схему менять нельзя, доступны только название, колонки для счёта и видимость колонок.',

            visibilityLabel: 'Видимые колонки',
            visibilityHint: 'Какие колонки показывать в таблице расчёта. Скрытая колонка продолжает считаться и участвовать в формулах других колонок и итогах — ' +
                'меняется только отображение, поэтому настройка доступна даже у используемой таблицы и сразу применяется ко всем её расчётам.',

            ratesLabel: 'Ставки',
            ratesHint: 'Проценты, которые таблица применяет при расчёте. Для неплательщика ПДВ укажите ПДВ = 0.',
            rateFields: {
                vatRate: 'ПДВ, %',
                epRate: 'Единый налог (ЄП), %',
                militaryRate: 'Военный сбор, %',
            },

            schemaLabel: 'Схема таблицы',
            schemaHint: (keys: string, consts: string) =>
                `Колонки расчёта и счёта. Тип «формула» — выражение над ключами других ` +
                `колонок (${keys}) и ставками ${consts}. Операторы + − * / ( ), сравнения, ` +
                `тернарный a ? b : c, функции round, floor, ceil, abs, min, max, ` +
                `if(усл, а, б). Скрытая колонка считается, но не показывается. ` +
                `«Итог» — колонка попадает в сумму внизу таблицы.`,
            columnKeyTitle: 'Ключ колонки — используется в формулах других колонок и итоговых полей. Например d*b означает «колонка d, умноженная на колонку b».',
            labelPlaceholder: 'Название',
            columnLabelTitle: 'Заголовок колонки — то, что увидит пользователь в шапке таблицы расчёта и в счёте. Например: «Цена без ПДВ», «Кол-во», «Од.виміру».',
            columnKindTitle: 'Тип колонки. «Ввод» — значение вписывается вручную в каждой строке (например количество, цена). «Формула» — значение вычисляется автоматически из других колонок, например d*b — цена умножить на количество.',
            kindInputOption: 'Ввод',
            kindFormulaOption: 'Формула',
            columnGroupTitle: 'Визуальная группа колонки — объединяет соседние колонки под общим заголовком «Расход»/«Доход» над таблицей. На расчёт не влияет, только на оформление. Например колонки «Цена без ПДВ», «ПДВ», «Сумма» на стороне закупки удобно объединить в «Расход».',
            groupNoneOption: 'Без группы',
            groupExpensesOption: 'Расход',
            groupIncomeOption: 'Доход',
            totalRoleTitle: 'Устаревшая привязка колонки к итоговой строке — используется только если ниже, в «Итоговых полях», ничего не задано. Например «Итог: прибыль» на колонке «ПРИБЫЛЬ» просуммирует её в строку «Прибыль» под таблицей. Если итоговые поля заданы явно — этот выбор игнорируется.',
            totalRoleOptions: {
                none: '—',
                expensesWithVat: 'Итог: расход с ПДВ',
                incomeWithVat: 'Итог: доход с ПДВ',
                profit: 'Итог: прибыль',
            },
            formulaPlaceholder: 'напр. g*b - d*b',
            formulaTitle: 'Выражение для расчёта этой колонки. Можно использовать ключи других колонок и ставки vat, ep, mil (в долях — vat=0.2 при ставке ПДВ 20%). ' +
                'Операторы + − * / ( ), сравнения == != < > <= >=, тернарный a ? b : c, функции round(x[,знаков]), floor, ceil, abs, min, max, if(усл, да, нет). ' +
                'Примеры: d*b*vat — сумма ПДВ (цена × кол-во × ставку); d*b + e — сумма с ПДВ; i == 0 ? 0 : l / i * 100 — процент, 0 при делении на ноль.',
            addColumnButton: '＋ Колонка',
            resetSchemaButton: 'Сбросить к стандартной',
            newColumnDefaultLabel: 'Новая колонка',
        },

        footerEditor: {
            sectionLabel: 'Итоговые поля (под таблицей)',
            hint: (keysSuffix: string, consts: string) =>
                `«Сумма» — суммирует выбранную колонку по всем строкам. «Формула» — ` +
                `выражение над уже объявленными выше итоговыми полями${keysSuffix} ` +
                `и ставками ${consts}. «Суффикс» дописывается после числа, например «%».`,
            keyTitle: 'Ключ итогового поля — используется в формулах других итоговых полей, объявленных ниже. Например margin со значением profit / incomeTax * 100 ссылается на поля profit и incomeTax.',
            labelTitle: 'Подпись строки в итогах под таблицей. Например: «Прибыль», «К оплате», «Итого с ПДВ».',
            kindTitle: 'Способ расчёта поля. «Сумма» — складывает значения выбранной колонки по всем строкам таблицы, например сумма колонки «СУММА» даст общую сумму дохода. «Формула» — вычисляет выражение над уже объявленными выше итоговыми полями и ставками vat/ep/mil, например profit / incomeTax * 100 — маржа в процентах.',
            kindSumOption: 'Сумма',
            columnTitle: 'Какую колонку таблицы суммировать по всем строкам. Например колонку «СУММА» (доход с ПДВ), чтобы получить общую сумму заказа.',
            columnPlaceholder: '— колонка —',
            suffixPlaceholder: 'Суффикс, напр. %',
            suffixTitle: 'Текст, который допишется сразу после числа в строке итогов. Например «%» для маржи (получится «24.44%») или « грн» для суммы.',
            colorTitle: 'Цвет текста этой строки итогов. Например зелёный для прибыли, красный для расхода; по умолчанию — нейтральный серый.',
            formulaPlaceholder: 'напр. profit / incomeTax * 100',
            formulaTitle: 'Выражение над итоговыми полями, объявленными выше (их ключи показаны в подсказке над списком), и ставками vat, ep, mil (в долях). ' +
                'Операторы + − * / ( ), сравнения, тернарный a ? b : c, функции round, floor, ceil, abs, min, max, if(усл, да, нет). ' +
                'Пример: incomeTax == 0 ? 0 : profit / incomeTax * 100 — маржа (прибыль делённая на сумму дохода), 0 если доход нулевой.',
            addFieldButton: '＋ Итоговое поле',
            newFieldDefaultLabel: 'Новое поле',
        },

        preview: {
            label: 'Предпросмотр',
            hint: 'Впишите данные прямо сюда, чтобы увидеть, как считает эта таблица — предпросмотр никуда не сохраняется.',
        },
    },
};

// A couple of backend messages interpolate a value instead of being a fixed
// string (`«${username}» has been deleted` from userController), so an
// exact-match table alone can't catch them — matched by pattern instead,
// checked before falling back to the raw text.
const DYNAMIC_API_MESSAGE_PATTERNS: Array<{ pattern: RegExp, translate: (m: RegExpMatchArray) => string }> = [
    { pattern: /^«(.+)» has been deleted$/, translate: (m) => `«${m[1]}» удалён` },
];

// What the toast interceptor (http/index.ts) runs a resolved backend message
// through before displaying it — exact dictionary match first, then the
// dynamic patterns above, else the original text unchanged.
export function translateApiMessage(raw: string): string {
    const known = locale.apiMessageTranslations[raw];
    if (known) return known;
    for (const { pattern, translate } of DYNAMIC_API_MESSAGE_PATTERNS) {
        const m = raw.match(pattern);
        if (m) return translate(m);
    }
    return raw;
}

export default locale;
